import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback } from 'react';

import traceUFOPress from '@atlaskit/react-ufo/trace-press';

import type { LensButtonProps } from '../../types';
import {
  getColorValue,
  getFocusRing,
  getOffsetFocusRing,
  getFontWeight,
  getSize,
  getTextSize,
  u,
} from '../../utilities';

import Icon from '../icon/icon';
import Loader from '../loader/loader';
import { shadows } from 'src/variables';

const sizesStyles = {
  small: {
    height: u(4),
    textSize: 'small',
    iconSize: 2,
    xSpace: u(1.5),
    radius: 'var(--lns-radius-150)',
  },
  medium: {
    height: u(4.5),
    textSize: 'medium',
    iconSize: 3,
    xSpace: u(2),
    radius: 'var(--lns-radius-175)',
  },
  large: {
    height: u(7),
    textSize: 'large',
    iconSize: 4,
    xSpace: u(2.5),
    radius: 'var(--lns-radius-250)',
  },
};

const hasLoaderStyles = props => css`
  ${props.hasLoader && `display: none`};
`;

const variantStyles = {
  neutral: {
    color: getColorValue('body'),
    background: 'transparent',
    borderColor: getColorValue('buttonBorder'),
    hover: getColorValue('backgroundHover'),
    active: getColorValue('backgroundActive'),
    floatingBackground: getColorValue('overlay'),
    floatingHover: getColorValue('overlayHover'),
    floatingActive: getColorValue('overlayActive'),
  },
  neutralSecondary: {
    color: getColorValue('body'),
    background: getColorValue('backgroundNeutral'),
    borderColor: null,
    hover: getColorValue('backgroundNeutralHover'),
    active: getColorValue('backgroundNeutralActive'),
  },
  primary: {
    color: getColorValue('white'),
    background: getColorValue('blurple'),
    borderColor: null,
    hover: getColorValue('primaryHover'),
    active: getColorValue('primaryActive'),
  },
  secondary: {
    color: getColorValue('primary'),
    background: getColorValue('highlight'),
    borderColor: null,
    hover: getColorValue('highlightHover'),
    active: null,
  },
  record: {
    color: getColorValue('white'),
    background: getColorValue('record'),
    borderColor: null,
    hover: getColorValue('recordHover'),
    active: getColorValue('recordActive'),
  },
  upgrade: {
    color: getColorValue('body'),
    background: getColorValue('upgrade'),
    borderColor: null,
    hover: getColorValue('upgradeHover'),
    active: getColorValue('upgradeActive'),
    focusRing: getFocusRing(),
  },
  danger: {
    color: getColorValue('bodyInverse'),
    background: getColorValue('danger'),
    borderColor: null,
    hover: getColorValue('dangerHover'),
    active: getColorValue('dangerActive'),
  },
  ai: {
    color: getColorValue('white'),
    background: getColorValue('ai-primary'),
    borderColor: null,
    hover: null,
    active: null,
  },
};

const statusStyles = props => ({
  enabled: css`
    cursor: pointer;
  `,
  disabled: css`
    ${props.ariaDisabled && `aria-disabled: true`};
    pointer-events: none;
    background-color: ${getColorValue('disabledBackground')};
    color: ${getColorValue('disabledContent')};
    border: none;
  `,
});

const widthStyles = props => ({
  auto: css`
    display: inline-flex;
    min-width: ${sizesStyles[props.size].height};
  `,
  full: css`
    display: flex;
    width: 100%;
  `,
  maxContent: css`
    display: inline-flex;
    width: max-content;
    min-width: max-content;
  `,
});

const iconGap = u(1);

const ButtonWrapper = styled.button<ButtonWrapperProps>`
  appearance: none;
  padding: 0
    ${props => (props.hasChildren ? sizesStyles[props.size].xSpace : 0)};
  font: inherit;
  text-decoration: none;
  transition:
    0.6s background,
    0.6s border-color;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  white-space: nowrap;
  ${getFontWeight('bold')};
  border-radius: ${props => sizesStyles[props.size].radius};
  // TODO: remove hasFullWidth after deprecation period
  ${props =>
    props.hasFullWidth ? 'display: flex; width: 100%' : 'display: inline-flex'};
  ${props => widthStyles(props)[props.width]};
  height: ${props => sizesStyles[props.size].height};
  ${props => getTextSize(sizesStyles[props.size].textSize)};
  ${props => props.isFloating && `box-shadow: ${shadows.medium}`};
  ${props =>
    props.disabled
      ? statusStyles(props).disabled
      : statusStyles(props).enabled};
  ${props =>
    !props.disabled &&
    `
    border: ${
      variantStyles[props.variant].borderColor
        ? `1px solid ${variantStyles[props.variant].borderColor}`
        : 'none'
    };
    background: ${
      props.isFloating && props.variant === 'neutral'
        ? variantStyles[props.variant].floatingBackground
        : variantStyles[props.variant].background
    };
    background-position: left;
    background-size: 125%;
    color: ${variantStyles[props.variant].color};
  `};

  &:hover {
    transition:
      0.3s background,
      0.3s border-color;
    background: ${props =>
      props.isFloating && props.variant === 'neutral'
        ? variantStyles[props.variant].floatingHover
        : variantStyles[props.variant].hover};
    background-position: 75% center;
  }

  &:active {
    transition:
      0s background,
      0s border-color;
    background: ${props =>
      props.isFloating && props.variant === 'neutral'
        ? variantStyles[props.variant].floatingActive
        : variantStyles[props.variant].active};
    background-position: right;
  }

  &:focus-visible {
    ${props =>
      props['aria-expanded'] ? 'outline: none;' : getOffsetFocusRing()};
  }

  &::-moz-focus-inner {
    border: 0;
  }
`;

const IconSection = styled.span<{
  paddingLeft: string;
  paddingRight: string;
  hasLoader: boolean;
}>`
  ${props => getSize('padding-left', props.paddingLeft)};
  ${props => getSize('padding-right', props.paddingRight)};
  ${hasLoaderStyles};
`;

const LogoImage = styled.img<{
  size: 'small' | 'medium' | 'large';
  hasSpacing: boolean;
  hasLoader: boolean;
}>`
  max-width: 1.45em;
  max-height: 1.45em;
  height: ${props => sizesStyles[props.size].height};
  width: ${props => sizesStyles[props.size].height};
  ${props => props.hasSpacing && `margin-right: 0.57em`};
  ${hasLoaderStyles};
`;

const LoaderSection = styled.span`
  position: relative;
  display: flex;
  align-items: center;
`;

const ChildrenSection = styled.span<{ hasLoader: boolean }>`
  ${hasLoaderStyles};
`;

const Button = ({
  size = 'medium',
  children,
  variant = 'neutral',
  hasFullWidth,
  width = 'auto',
  icon,
  iconPosition = 'left',
  iconBefore,
  iconAfter,
  logoSrc,
  hasLoader,
  isDisabled,
  ariaDisabled,
  htmlTag = 'button',
  interactionName,
  onClick,
  refHandler,
  ...props
}: ButtonProps &
  React.ComponentProps<typeof ButtonWrapper> &
  Omit<
    LensButtonProps,
    keyof ButtonProps & React.ComponentProps<typeof ButtonWrapper>
  >) => {
  const legacyIconLeft = icon && iconPosition === 'left' ? icon : null;
  const iconBeforeElement =
    iconBefore || legacyIconLeft ? (
      <IconSection
        hasLoader={hasLoader}
        paddingLeft="0"
        paddingRight={children ? iconGap : '0'}
      >
        <Icon
          icon={iconBefore || legacyIconLeft}
          color="currentColor"
          size={sizesStyles[size].iconSize}
        />
      </IconSection>
    ) : null;
  const legacyIconRight = icon && iconPosition === 'right' ? icon : null;
  const iconAfterElement =
    iconAfter || legacyIconRight ? (
      <IconSection
        hasLoader={hasLoader}
        paddingLeft={children ? iconGap : '0'}
        paddingRight="unset"
      >
        <Icon
          icon={legacyIconRight || iconAfter}
          color="currentColor"
          size={sizesStyles[size].iconSize}
        />
      </IconSection>
    ) : null;

  const onClickWithPressTracing = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (interactionName) {
        traceUFOPress(interactionName);
      }
      onClick?.(event);
    },
    [onClick, interactionName]
  );

  return (
    <ButtonWrapper
      size={size}
      variant={variant}
      hasFullWidth={hasFullWidth}
      width={width}
      icon={icon}
      iconPosition={iconPosition}
      logoSrc={logoSrc}
      disabled={isDisabled}
      ariaDisabled={ariaDisabled}
      as={htmlTag}
      hasChildren={children}
      ref={ref => refHandler && refHandler(ref)}
      {...props}
      onClick={
        interactionName === undefined ? onClick : onClickWithPressTracing
      }
    >
      {hasLoader && (
        <LoaderSection>
          <Loader color="currentColor" />
        </LoaderSection>
      )}
      {iconBeforeElement}
      {logoSrc && (
        <LogoImage
          // Assumes image is only referencing information written within the button.
          alt=""
          hasSpacing={Boolean(children)}
          src={logoSrc}
          size={size}
          // Assumes all images are 1:1 aspectRatio
          height={sizesStyles[size].height}
          width={sizesStyles[size].height}
          hasLoader={hasLoader}
        />
      )}
      <ChildrenSection hasLoader={hasLoader}>{children}</ChildrenSection>
      {iconAfterElement}
    </ButtonWrapper>
  );
};

type Variant =
  | 'neutral'
  | 'neutralSecondary'
  | 'primary'
  | 'secondary'
  | 'record'
  | 'upgrade'
  | 'danger'
  | 'ai';

type Width = 'auto' | 'full' | 'maxContent';
interface ButtonProps {
  size?: 'small' | 'medium' | 'large';
  variant?: Variant;
  children?: React.ReactNode;
  isDisabled?: boolean;
  ariaDisabled?: boolean;
  /** @deprecated Use width={'full'} instead */
  hasFullWidth?: boolean;
  width?: Width;
  /** @deprecated Use iconBefore or iconAfter instead */
  icon?: React.ReactNode;
  /** @deprecated Use iconBefore or iconAfter instead */
  iconPosition?: 'left' | 'right';
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  logoSrc?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  hasLoader?: boolean;
  isFloating?: boolean;
  htmlTag?: 'button' | 'a';
  href?: string;
  download?: string;
  target?: string;
  type?: string;
  refHandler?: (ref: HTMLButtonElement) => void;
  /** Used to enable press tracing. See more at https://developer.atlassian.com/platform/ufo/react-ufo/react-ufo/press-interactions/#implementation */
  interactionName?: string;
  /** @deprecated Usage of className results in non-standard components */
  className?: string;
  /** @deprecated Usage of style results in non-standard components */
  style?: any;
}

interface ButtonWrapperProps {
  hasNoPaddingRight?: boolean;
  hasNoPaddingLeft?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: Variant;
  hasFullWidth?: boolean;
  width?: Width;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  logoSrc?: string;
  as?: string;
  disabled?: boolean;
  ariaDisabled?: boolean;
  buttonPadding?: number | string;
  hasChildren?: React.ReactNode;
  isFloating?: boolean;
}

export const availableSizes = Object.keys(sizesStyles);
export const availableVariants = Object.keys(variantStyles);
export const availableHtmlTags = ['button', 'a'];
export const availableIconPositions = ['left', 'right'];

export default Button;
